import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Toolbar,
  Typography,
  Stack,
  TextField,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import DeleteIcon from "@mui/icons-material/Delete";
import axios from "axios";
import { DataGrid } from "@mui/x-data-grid";
import Swal from "sweetalert2";

export default function TransportEnrollment() {
  const [feeHeads, setFeeHeads] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [villages, setVillages] = useState([]);
  const [form, setForm] = useState({
    acyear_id: "",
    student_id: "",
    student_rollno: "",
    route_id: "",
    village_id: "",
    fee_head_id: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const showSnackbar = (msg, sev = "success") => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(sev);
    setSnackbarOpen(true);
  };
  const getErrorMessage = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => {
    try {
      const u = sessionStorage.getItem("user");
      return u ? JSON.parse(u) : null;
    } catch (err) {
      return null;
    }
  }, []);
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    if (!user) return;
    const fetchStudent = async () => {
      try {
        const url =
          `${API_BASE_URL}/api/students/by-email/${user.client_id}/` +
          `${encodeURIComponent(user.user_email)}?college_id=${encodeURIComponent(
            user.college_id || ""
          )}`;
        const res = await axios.get(url, {
          headers: getAuthHeaders(),
        });
        const data = res.data?.data || res.data || null;
        if (data && data.student_id) {
          setForm((prev) => ({
            ...prev,
            student_id: data.student_id,
            student_rollno: data.student_rollno || "",
            college_id: data.college_id || user.college_id || "",
          }));
        }
      } catch (err) {
        showSnackbar(getErrorMessage(err, "Unable to load student details"), "error");
      }
    };
    fetchStudent();
  }, [user, API_BASE_URL]);

  useEffect(() => {
    if (!user || !form.college_id) return;
    const fetchAcademicYear = async () => {
      try {
        const url = `${API_BASE_URL}/api/acyear/by-college/${user.client_id}/${form.college_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = res.data?.data || res.data || null;
        if (Array.isArray(data) && data.length > 0) {
          setForm((prev) => ({ ...prev, acyear_id: data[0].acyear_id }));
        }
      } catch (err) {
        showSnackbar(getErrorMessage(err, "Unable to load academic year"), "error");
      }
    };
    fetchAcademicYear();
  }, [user, form.college_id, API_BASE_URL]);

  useEffect(() => {
    if (!user || !form.college_id) return;
    const fetchFeeHeads = async () => {
      try {
        const url = `${API_BASE_URL}/api/fee-heads/${user.client_id}/${form.college_id}/transport`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        const data = Array.isArray(res.data?.data) ? res.data.data : [];
        setFeeHeads(data);
      } catch (err) {
        setFeeHeads([]);
        showSnackbar(getErrorMessage(err, "Unable to load fee heads"), "error");
      }
    };
    const fetchRoutes = async () => {
      try {
        const url = `${API_BASE_URL}/api/transport-routes/${user.client_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        setRoutes(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        setRoutes([]);
        showSnackbar(getErrorMessage(err, "Unable to load routes"), "error");
      }
    };
    fetchFeeHeads();
    fetchRoutes();
  }, [user, form.college_id, API_BASE_URL]);

  useEffect(() => {
    if (!user || !form.college_id) return;
    const fetchVillages = async (route_id) => {
      if (!route_id) {
        setVillages([]);
        return;
      }
      try {
        const url = `${API_BASE_URL}/api/transport-villages/by-route/${user.client_id}/${form.college_id}/${route_id}`;
        const res = await axios.get(url, { headers: getAuthHeaders() });
        setVillages(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (err) {
        setVillages([]);
        showSnackbar(getErrorMessage(err, "Unable to load villages"), "error");
      }
    };
    fetchVillages(form.route_id);
    setForm((prev) => ({ ...prev, village_id: "" })); // reset village when route changes
  }, [form.route_id, user, form.college_id, API_BASE_URL]);

  const fetchEnrollments = useCallback(async () => {
    if (!form.student_id || !user) return;
    setLoading(true);
    try {
      const clientId = Number(user.client_id);
      if (isNaN(clientId)) throw new Error("Invalid clientId");
      const url = `${API_BASE_URL}/api/transport-enrollment/${clientId}?student_id=${form.student_id}`;
      const res = await axios.get(url, {
        headers: getAuthHeaders(),
      });
      const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
      setRows(data);
    } catch (err) {
      setRows([]);
      showSnackbar(getErrorMessage(err, "Unable to load transport enrollments"), "error");
    }
    setLoading(false);
  }, [form.student_id, user, API_BASE_URL]);

  useEffect(() => {
    if (!form.student_id) return;
    fetchEnrollments();
  }, [form.student_id, fetchEnrollments]);

  const formatDate = (val) => {
    if (!val) return "";
    const d = typeof val === "string" ? new Date(val) : val;
    return d && !isNaN(d) ? d.toLocaleDateString() : "";
  };

  const getFeeHeadName = (feeHeadId, row) => {
    if (row?.fee_head?.fee_head_name) return row.fee_head.fee_head_name;
    const feeHead = feeHeads.find((fh) => String(fh.fee_head_id) === String(feeHeadId));
    return feeHead?.fee_head_name || feeHeadId || "";
  };

  const handleDelete = async (row) => {
    if (!row?.transport_enrollment_id || !user?.client_id) {
      showSnackbar("Enrollment details are missing", "error");
      return;
    }
    const result = await Swal.fire({
      title: "Delete enrollment?",
      text: "This will remove the transport enrollment from the active list.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      setLoading(true);
      await axios.delete(
        `${API_BASE_URL}/api/transport-enrollment/${user.client_id}/${row.transport_enrollment_id}`,
        { headers: getAuthHeaders() }
      );
      showSnackbar("Enrollment deleted successfully");
      fetchEnrollments();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "Delete failed"), "error");
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      field: "serial_no",
      headerName: "S.No",
      width: 80,
      sortable: false,
      filterable: false,
      valueGetter: (params) => params.api.getRowIndex(params.id) + 1,
    },
    {
      field: "student_rollno",
      headerName: "Roll No",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "route_name",
      headerName: "Route Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "village_name",
      headerName: "Village Name",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "fee_head_id",
      headerName: "Fee Head",
      flex: 1,
      minWidth: 160,
      valueGetter: (params) => getFeeHeadName(params?.row?.fee_head_id, params?.row),
    },
    {
      field: "created_on",
      headerName: "Created On (DB)",
      flex: 1,
      minWidth: 140,
      valueGetter: (params) => formatDate(params?.row?.created_on),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Tooltip title="Delete enrollment">
          <IconButton color="error" size="medium" onClick={() => handleDelete(params.row)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // Form submit handler with SweetAlert confirmation
  const handleEnroll = async () => {
    if (!form.fee_head_id || !form.route_id || !form.village_id) {
      showSnackbar("Please select all fields", "error");
      return;
    }
    if (!user) {
      showSnackbar("User not found", "error");
      return;
    }
    if (!form.acyear_id || isNaN(Number(form.acyear_id))) {
      showSnackbar("Academic Year is missing or invalid. Please contact admin.", "error");
      return;
    }
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to enroll?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, enroll",
      cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;
    try {
      setLoading(true);
      const selectedRoute = routes.find((r) => String(r.route_id) === String(form.route_id));
      const route_name = selectedRoute ? selectedRoute.route_name : "";
      const selectedVillage = villages.find(
        (v) => String(v.village_id) === String(form.village_id)
      );
      const village_name = selectedVillage ? selectedVillage.village_name : "";
      const payload = {
        clientId: user.client_id,
        acyear_id: Number(form.acyear_id),
        student_id: Number(form.student_id),
        student_rollno: form.student_rollno,
        route_id: Number(form.route_id),
        route_name,
        village_id: Number(form.village_id),
        village_name,
        fee_head_id: Number(form.fee_head_id),
      };
      if (user && user.user_email) {
        payload.created_by = user.user_email;
      }
      payload.created_on = new Date().toISOString();
      const url = `${API_BASE_URL}/api/transport-enrollment`;
      await axios.post(url, payload, { headers: getAuthHeaders() });
      showSnackbar("Enrollment successful");
      setForm((f) => ({
        ...f,
        fee_head_id: "",
        route_id: "",
        village_id: "",
      }));
      fetchEnrollments();
    } catch (err) {
      showSnackbar(getErrorMessage(err, "Enrollment failed"), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h5" sx={{ flexGrow: 1 }}>
            TRANSPORT ENROLLMENT
          </Typography>
        </Toolbar>
        <Box p={2}>
          <Stack spacing={2}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-end">
              <TextField
                select
                margin="normal"
                label="Route Name"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.route_id}
                onChange={(e) => setForm((p) => ({ ...p, route_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Route</option>
                {routes.map((r) => (
                  <option key={r.route_id} value={r.route_id}>
                    {r.route_name}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                margin="normal"
                label="Village Name"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.village_id}
                onChange={(e) => setForm((p) => ({ ...p, village_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Village</option>
                {villages.map((v) => (
                  <option key={v.village_id} value={v.village_id}>
                    {v.village_name}
                  </option>
                ))}
              </TextField>
              <TextField
                select
                margin="normal"
                label="Fee Head"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={form.fee_head_id}
                onChange={(e) => setForm((p) => ({ ...p, fee_head_id: e.target.value }))}
                SelectProps={{ native: true }}
              >
                <option value="">Select Fee Head</option>
                {feeHeads.map((fh) => (
                  <option key={fh.fee_head_id} value={fh.fee_head_id}>
                    {fh.fee_head_name}
                  </option>
                ))}
              </TextField>
              <Button
                variant="contained"
                color="primary"
                onClick={handleEnroll}
                disabled={loading}
                sx={{
                  height: "56px",
                  minWidth: "160px",
                  ml: { sm: 2, xs: 0 },
                  color: "#fff",
                  "&:hover": { color: "#fff" },
                  "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "#fff" },
                }}
              >
                ENROLL NOW !
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
      <Box mt={3}>
        <DataGrid
          rows={rows}
          columns={columns}
          autoHeight
          loading={loading}
          getRowId={(row) => row.transport_enrollment_id}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
